import type { CSSProperties, JSX } from 'react';
import { Grid, Space, Spin, Switch, Tag, Typography } from 'antd';

import type { ClinicalRecord } from '@erp/clinical-history/api/clinicalHistory.api';

import {
  buildClinicalRecordDateLabel,
  buildClinicalRecordNotePreview,
  buildClinicalRecordStaffLabel,
  buildCompactClinicalRecordSpaceLabel,
  buildServiceLinesLabel,
  createActivationKeyDown,
  hasClinicalHistoryNote,
} from './historyHelpers';

const { Text } = Typography;

const tableStyle: CSSProperties = {
  width: '100%',
  maxWidth: '100%',
  tableLayout: 'fixed',
  borderCollapse: 'separate',
  borderSpacing: '0 4px',
};

const thStyle: CSSProperties = {
  padding: '0 0 4px',
  fontSize: 12,
  fontWeight: 600,
  textAlign: 'left',
  color: 'rgba(0,0,0,0.45)',
  borderBottom: '1px solid rgba(5,5,5,0.06)',
};

const cellStyle: CSSProperties = {
  padding: 0,
  verticalAlign: 'middle',
  minWidth: 0,
};

const tagStyle: CSSProperties = {
  marginInlineEnd: 0,
  maxWidth: '100%',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const serviceTextStyle: CSSProperties = {
  display: 'block',
  minWidth: 0,
  overflowWrap: 'anywhere',
  whiteSpace: 'normal',
  fontSize: 13,
};

const notePreviewStyle: CSSProperties = {
  display: 'block',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  maxWidth: '100%',
  fontSize: 12,
};

const tagGroupStyle: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 4,
  minWidth: 0,
};

interface RecentClinicalRecordsTableProps {
  records: ClinicalRecord[];
  loading: boolean;
  appointmentId: number;
  spaceNameById?: Map<number, string>;
  onOpenRecord: (record: ClinicalRecord) => void;
  showAllHistory: boolean;
  onShowAllHistoryChange: (checked: boolean) => void;
  hasAnyRecords: boolean;
}

export function RecentClinicalRecordsTable({
  records,
  loading,
  appointmentId,
  spaceNameById,
  onOpenRecord,
  showAllHistory,
  onShowAllHistoryChange,
  hasAnyRecords,
}: RecentClinicalRecordsTableProps): JSX.Element | null {
  const screens = Grid.useBreakpoint();

  if (loading) {
    return <Spin size="small" />;
  }

  if (!hasAnyRecords) {
    return null;
  }

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, flexWrap: 'wrap', gap: 8 }}>
        <Text strong>
          Últimos turnos del cliente
        </Text>
        <Space size="small">
          <Text type="secondary" style={{ fontSize: 12 }}>Mostrar sin historia cerrada</Text>
          <Switch
            checked={showAllHistory}
            onChange={onShowAllHistoryChange}
            size="small"
          />
        </Space>
      </div>
      {records.length === 0 ? (
        <Text type="secondary" style={{ fontSize: 13, fontStyle: 'italic', display: 'block', marginTop: 8 }}>
          No hay turnos con historia clínica cerrada.
        </Text>
      ) : (
      <div style={{ overflowX: 'auto' }}>
        <table style={tableStyle}>
          <colgroup>
            <col style={{ width: screens.md ? '18%' : '28%' }} />
            <col style={{ width: screens.md ? '24%' : '32%' }} />
            <col style={{ width: screens.md ? '24%' : '40%' }} />
            {screens.md ? <col /> : null}
          </colgroup>
          <thead>
            <tr>
              <th style={thStyle}>Fecha</th>
              <th style={thStyle}>Sector · Prof.</th>
              <th style={thStyle}>Servicios</th>
              {screens.md ? <th style={thStyle}>Historia clínica</th> : null}
            </tr>
          </thead>
          <tbody>
            {records.map((record) => {
              const isCurrentAppointment = record.id === appointmentId;
              const isInteractive = hasClinicalHistoryNote(record);
              const handleOpenRecord = () => onOpenRecord(record);

              return (
                <tr
                  key={record.id}
                  role={isInteractive ? 'button' : undefined}
                  tabIndex={isInteractive ? 0 : undefined}
                  style={{
                    cursor: isInteractive ? 'pointer' : 'default',
                    opacity: isInteractive ? 1 : 0.5,
                  }}
                  onClick={isInteractive ? handleOpenRecord : undefined}
                  onKeyDown={isInteractive ? createActivationKeyDown(handleOpenRecord) : undefined}
                >
                  <td style={cellStyle}>
                    <Tag color={isCurrentAppointment ? 'gold' : 'blue'} style={tagStyle}>
                      {buildClinicalRecordDateLabel(record)}
                    </Tag>
                  </td>
                  <td style={cellStyle}>
                    <div style={tagGroupStyle}>
                      <Tag style={tagStyle}>
                        {buildCompactClinicalRecordSpaceLabel(record, spaceNameById)}
                      </Tag>
                      <Tag color="cyan" style={tagStyle}>
                        {buildClinicalRecordStaffLabel(record)}
                      </Tag>
                    </div>
                  </td>
                  <td style={cellStyle}>
                    <Text style={serviceTextStyle}>{buildServiceLinesLabel(record.service_lines)}</Text>
                  </td>
                  {screens.md ? (
                    <td style={cellStyle}>
                      {record.latest_history_note ? (
                        <Text style={notePreviewStyle}>{buildClinicalRecordNotePreview(record)}</Text>
                      ) : (
                        <Text type="secondary" style={{ fontSize: 12, fontStyle: 'italic' }}>
                          -
                        </Text>
                      )}
                    </td>
                  ) : null}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      )}
    </>
  );
}

export default RecentClinicalRecordsTable;
